import { ChangeEvent, FormEvent, FunctionComponent, ReactElement, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Stack, Typography } from '@mui/material';
import PasswordField from '@/components/ui/form-inputs/PasswordField';
import LoadingButton from '@/components/ui/loading-button';
import { isValidEmail, requiredField } from '@/shared/utils/validation';
import {
	ACCOUNT_INACTIVE,
	INVALID_CREDENTIALS,
	INVALID_EMAIL,
	TEMP_PASSWORD_EXPIRED,
	TEMP_PASSWORD_MUST_CHANGE,
} from '@/shared/utils/validation/constants';
import { useLogin } from '@/queries/auth';
import TextField from '@/components/ui/form-inputs/TextField';
import { LOGIN_FORM_KEY } from '@/shared/constants/form-keys';
import AuthCardLayout from '../layouts/auth-card-layout/AuthCardLayout';
import AuthLayout from '../layouts/auth-layout/AuthLayout';
import ForcedPasswordChangeForm from '../components/ForcedPasswordChangeForm';

const Login: FunctionComponent = (): ReactElement => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');

	const [emailError, setEmailError] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const [expiredError, setExpiredError] = useState('');

	// Temp password still valid but unused: the login screen swaps to the
	// forced-change form, carrying the email + temp password client-side since
	// there's no session yet (portal-be PR #13, complete-forced-change).
	const [forcedChange, setForcedChange] = useState<{
		email: string;
		temporaryPassword: string;
	} | null>(null);

	const { isLoading, mutate: onLogin } = useLogin({
		onError: (message) => {
			if (message === TEMP_PASSWORD_EXPIRED || message === ACCOUNT_INACTIVE) {
				setExpiredError(message);
				return;
			}

			if (message === TEMP_PASSWORD_MUST_CHANGE) {
				setForcedChange({ email, temporaryPassword: password });
				return;
			}

			setExpiredError('');
			setEmailError(INVALID_CREDENTIALS);
			setPasswordError(INVALID_CREDENTIALS);
		},
	});

	const validateEmail = (): boolean => {
		if (email.length === 0) {
			setEmailError(requiredField('Email'));
			return false;
		}

		if (!isValidEmail(email)) {
			setEmailError(INVALID_EMAIL);
			return false;
		}

		setEmailError('');
		return true;
	};

	const validatePassword = (): boolean => {
		if (password.length === 0) {
			setPasswordError(requiredField('Password'));
			return false;
		}

		setPasswordError('');
		return true;
	};

	const loginAction = (event: FormEvent) => {
		event.preventDefault();
		setExpiredError('');
		const isEmailValid = validateEmail();
		const isPasswordValid = validatePassword();

		if (!isEmailValid || !isPasswordValid) {
			return;
		}

		onLogin({
			request: {
				request: { email, password },
				formKey: LOGIN_FORM_KEY,
			},
		});
	};

	if (forcedChange) {
		return (
			<ForcedPasswordChangeForm
				email={forcedChange.email}
				temporaryPassword={forcedChange.temporaryPassword}
				onBackToLogin={() => setForcedChange(null)}
			/>
		);
	}

	return (
		<Box component="section" role="main">
			<AuthLayout>
				<AuthCardLayout title="Welcome!">
					<Box component="form" onSubmit={loginAction} mt={3}>
						<Stack direction="column" spacing={3}>
							<Stack direction="column" spacing={2}>
								{expiredError && (
									<Typography variant="body2" color="error">
										{expiredError}
									</Typography>
								)}
								<TextField
									id="email"
									name="email"
									aria-label="Email"
									label="Email"
									type="email"
									formKey={LOGIN_FORM_KEY}
									fullWidth
									value={email}
									onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
									clientErrors={emailError}
								/>
								<PasswordField
									id="password"
									name="password"
									aria-label="Password"
									label="Password"
									formKey={LOGIN_FORM_KEY}
									required
									fullWidth
									value={password}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										setPassword(event.target.value)
									}
									clientErrors={passwordError}
								/>
								<Link component={RouterLink} to="/auth/forgot-password">
									Forgot Password?
								</Link>
							</Stack>

							<LoadingButton
								loading={isLoading}
								variant="contained"
								size="large"
								fullWidth
								ariaLabel="Login progress"
								type="submit"
							>
								Log In
							</LoadingButton>
						</Stack>
					</Box>
				</AuthCardLayout>
			</AuthLayout>
		</Box>
	);
};

export default Login;
